import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import TranslationProgressBanner from "../components/TranslationProgressBanner";
import { useAuth } from "../contexts/AuthContext";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Flag, Eye, MessageSquare, Heart, Quote, Edit3, Trash2, Award, Pin, Lock,
  Bookmark, BookmarkCheck, Bold, Italic, List as ListIcon, Link2,
  Image as ImageIcon, X, EyeOff, Shield, Plus, AtSign, FolderInput,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Bilde-thumbnail: bruk thumbnail_url direkte hvis satt (direkte R2/CDN),
// ellers fall tilbake til <AuthImage> som henter signert URL for /raw.
const ThumbImg = ({ img, alt = "", className = "" }) => {
  if (img?.thumbnail_url) {
    return <img src={img.thumbnail_url} alt={alt} className={className} loading="lazy" />;
  }
  if (img?.id) {
    return <AuthImage imageId={img.id} alt={alt} className={className} />;
  }
  return <div className={`bg-zinc-900 ${className}`} />;
};

// Åpne full-versjonen i nytt vindu ved å hente signert URL først.
const openFullImage = async (imageId) => {
  try {
    const r = await api.get(`/media/sign`, { params: { kind: "image", id: imageId } });
    if (r.status !== 200) return;
    window.open(`${BACKEND_URL}${r.data.url}`, "_blank", "noopener,noreferrer");
  } catch {
    /* stille */
  }
};

// ---------- Rapport ---------- //
const ReportButton = ({ targetType, targetId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("ulovlig_innhold");
  const [details, setDetails] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
      setDone(true);
      setTimeout(() => { setOpen(false); setDone(false); setDetails(""); }, 1500);
    } catch (e) { setError(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="font-meta text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
                data-testid={`report-${targetType}-${targetId}`}>
          <Flag className="h-3 w-3" /> {t("thread.report")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--noir-elev)] border-white/10 text-[var(--cream)] rounded-sm">
        <DialogHeader><DialogTitle className="font-fraunces italic text-2xl">{t("thread.report_content_title")}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-[var(--magenta)] font-fraunces italic py-4">{t("thread.report_thanks")}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="font-meta mb-2">{t("thread.report_reason_label")}</div>
              <select value={reason} onChange={(e)=>setReason(e.target.value)} className="input-editorial">
                <option value="ulovlig_innhold">{t("thread.report_reason_illegal")}</option>
                <option value="manglende_samtykke">{t("thread.report_reason_no_consent")}</option>
                <option value="mindreaarig">{t("thread.report_reason_minor")}</option>
                <option value="trakassering">{t("thread.report_reason_harassment")}</option>
                <option value="spam">{t("thread.report_reason_spam")}</option>
                <option value="annet">{t("thread.report_reason_other")}</option>
              </select>
            </div>
            <div>
              <div className="font-meta mb-2">{t("thread.report_details_label")}</div>
              <textarea value={details} onChange={(e)=>setDetails(e.target.value)} rows={3} className="input-editorial" />
            </div>
            {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">{error}</div>}
            <DialogFooter>
              <button onClick={submit} className="btn-magenta">{t("thread.report_submit")}</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Markdown-toolbar ---------- //
const MarkdownToolbar = ({ textareaRef, value, onChange, onAddImage }) => {
  const { t } = useTranslation();
  const wrap = (before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const next = value.slice(0, start) + before + sel + after + value.slice(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };
  const buttons = [
    { Icon: Bold, label: t("thread.md_bold"), onClick: () => wrap("**") },
    { Icon: Italic, label: t("thread.md_italic"), onClick: () => wrap("*") },
    { Icon: ListIcon, label: t("thread.md_list"), onClick: () => wrap("\n- ", "") },
    { Icon: Quote, label: t("thread.quote"), onClick: () => wrap("\n> ", "") },
    { Icon: Link2, label: t("thread.md_link"), onClick: () => wrap("[", "](https://)") },
  ];
  return (
    <div className="flex items-center gap-1 mb-2 border-b border-white/10 pb-2" data-testid="md-toolbar">
      {buttons.map(({ Icon, label, onClick }) => (
        <button
          key={label}
          type="button"
          onClick={onClick}
          title={label}
          className="p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
      {onAddImage && (
        <button
          type="button"
          onClick={onAddImage}
          title={t("thread.md_image")}
          className="p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
          data-testid="md-add-image"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </button>
      )}
      <span className="ml-auto font-meta text-[10px]">{t("thread.md_hint")}</span>
    </div>
  );
};

// ---------- Bilde-velger fra eget album ---------- //
const ImagePicker = ({ open, onClose, onPick, alreadyPicked }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/me/images?limit=80").then((r) => setImages(r.data)).catch(() => setImages([]));
  }, [open]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--noir-elev)] border-white/10 text-[var(--cream)] rounded-sm max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-fraunces italic text-2xl">{t("thread.picker_title")}</DialogTitle>
          <p className="font-meta text-xs text-[var(--cream-soft)]">{t("thread.picker_hint")}</p>
        </DialogHeader>
        {images === null ? (
          <div className="font-meta text-[var(--cream-soft)] py-6">{t("thread.picker_loading")}</div>
        ) : images.length === 0 ? (
          <div className="font-fraunces italic text-[var(--cream-soft)] py-6">
            {t("thread.picker_no_images_prefix")}{" "}
            <Link to="/albums" className="text-[var(--magenta)]">{t("thread.picker_upload_first")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[55vh] overflow-y-auto" data-testid="image-picker-grid">
            {images.map((im) => {
              const picked = alreadyPicked.includes(im.id);
              return (
                <button
                  key={im.id}
                  type="button"
                  onClick={() => onPick(im.id)}
                  className={`relative aspect-square overflow-hidden rounded-sm border ${
                    picked ? "border-[var(--magenta)]" : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`pick-image-${im.id}`}
                >
                  <ThumbImg img={im} alt="" className="h-full w-full object-cover" />
                  {picked && (
                    <div className="absolute inset-0 bg-[var(--magenta)]/30 flex items-center justify-center">
                      <span className="font-meta text-xs bg-[var(--magenta)] text-black px-2 py-0.5">{t("thread.picker_selected")}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="image-picker-close">{t("thread.picker_done")}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Innleggs-tekst med mention-link-rendering ---------- //
const PostBody = ({ body, mentions = [] }) => {
  // Bygg lookup over godkjente mentions for raskt oppslag
  const accepted = useMemo(() => {
    const m = new Map();
    (mentions || []).forEach((x) => m.set((x.username || "").toLowerCase(), x));
    return m;
  }, [mentions]);

  const renderText = (text) => {
    if (!text) return text;
    const parts = String(text).split(/(@[a-zA-Z0-9_]{3,30})/g);
    return parts.map((seg, i) => {
      if (seg.startsWith("@")) {
        const uname = seg.slice(1).toLowerCase();
        if (accepted.has(uname)) {
          return (
            <Link
              key={i}
              to={`/profile/${accepted.get(uname).username}`}
              className="text-[var(--magenta)] hover:underline"
              data-testid={`mention-link-${uname}`}
            >
              {seg}
            </Link>
          );
        }
        // pending/avslått: vises som vanlig tekst (jf. avgjørelse fra produkteier)
        return <span key={i}>{seg}</span>;
      }
      return <span key={i}>{seg}</span>;
    });
  };

  return (
    <div className="prose-novelle font-fraunces text-lg text-[var(--cream)] leading-[1.7] break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline-offset-4 hover:underline" />,
          blockquote: ({ node, ...props }) => <blockquote {...props} className="border-l-2 border-[var(--magenta)] pl-4 my-4 italic text-[var(--cream-soft)]" />,
          ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 space-y-1 my-3" />,
          ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 space-y-1 my-3" />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code {...props} className="bg-white/8 px-1 py-0.5 text-sm font-mono" />
              : <code {...props} className="block bg-[var(--noir-elev)] p-3 my-3 text-sm font-mono whitespace-pre-wrap" />,
          p: ({ node, children, ...props }) => <p {...props} className="my-3">{React.Children.map(children, (c) => typeof c === "string" ? renderText(c) : c)}</p>,
          li: ({ node, children, ...props }) => <li {...props}>{React.Children.map(children, (c) => typeof c === "string" ? renderText(c) : c)}</li>,
          strong: ({ node, ...props }) => <strong {...props} className="font-bold text-[var(--cream)]" />,
          em: ({ node, ...props }) => <em {...props} className="italic" />,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
};

// ---------- Bilde-vedlegg (paywall-aware) ---------- //
const PostImages = ({ images = [] }) => {
  const { t } = useTranslation();
  if (!images.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 max-w-xl" data-testid="post-images">
      {images.map((im) =>
        im.locked ? (
          <div
            key={im.id}
            className="aspect-square bg-white/5 border border-white/10 rounded-sm flex flex-col items-center justify-center gap-2 text-[var(--cream-soft)]"
            data-testid={`locked-image-${im.id}`}
          >
            <Lock className="h-5 w-5 text-[var(--magenta)]" />
            <span className="font-meta text-[10px] uppercase tracking-wider">
              {im.requires_tier === "creator" ? t("thread.requires_creator") : im.requires_tier === "vip" ? t("thread.requires_vip") : t("thread.requires_access")}
            </span>
          </div>
        ) : (
          <button
            key={im.id}
            type="button"
            onClick={() => openFullImage(im.id)}
            className="block aspect-square overflow-hidden rounded-sm border border-white/10 hover:border-[var(--magenta)] transition-colors"
            data-testid={`post-image-${im.id}`}
          >
            <ThumbImg img={im} alt={im.caption || ""} className="h-full w-full object-cover" />
          </button>
        )
      )}
    </div>
  );
};

// ---------- Hovedkomponent ---------- //
export default function ThreadView() {
  const { t } = useTranslation();
  const { threadId } = useParams();
  const { user, linkedCouple, postingAsCoupleId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [imageIds, setImageIds] = useState([]); // for nytt svar
  const [editImageIds, setEditImageIds] = useState([]); // for redigering
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editPickerFor, setEditPickerFor] = useState(null); // post-id når picker åpnes for edit
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [showMods, setShowMods] = useState(false);
  const [newModName, setNewModName] = useState("");
  const [showMove, setShowMove] = useState(false);
  const [moveForumId, setMoveForumId] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [forumList, setForumList] = useState([]); // [{category, forums: [...]}]
  const [moving, setMoving] = useState(false);
  const replyRef = useRef(null);
  const editRef = useRef(null);

  const load = () => api.get(`/threads/${threadId}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  useEffect(() => {
    if (!data) return;
    const firstUnread = data.posts.find((p) => p.is_unread);
    if (firstUnread) {
      setTimeout(() => {
        const el = document.getElementById(`post-${firstUnread.id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
    // eslint-disable-next-line
  }, [data?.thread?.id]);

  const isLoggedIn = user && user !== false;
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const quote = (post) => {
    const quoted = post.body.split("\n").map((l) => `> ${l}`).join("\n");
    setBody((prev) => `${prev ? prev + "\n\n" : ""}**@${post.author?.username}** ${t("thread.quote_attribution")}\n${quoted}\n\n`);
    setTimeout(() => {
      replyRef.current?.focus();
      replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const submitReply = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLoggedIn) return navigate("/login", { state: { from: `/thread/${threadId}` } });
    setLoading(true);
    try {
      const payload = { body, image_ids: imageIds };
      if (postingAsCoupleId) payload.as_couple_id = postingAsCoupleId;
      await api.post(`/threads/${threadId}/posts`, payload);
      setBody("");
      setImageIds([]);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const postingAsCouple = !!postingAsCoupleId && linkedCouple && postingAsCoupleId === linkedCouple.id;

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditBody(p.body);
    setEditImageIds(p.image_ids || (p.images || []).map((i) => i.id));
  };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); setEditImageIds([]); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/posts/${id}`, { body: editBody, image_ids: editImageIds });
      cancelEdit();
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const deletePost = async (id) => {
    if (!window.confirm(t("thread.delete_confirm"))) return;
    try { await api.delete(`/posts/${id}`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const deleteThread = async () => {
    if (!window.confirm(t("thread.delete_thread_full_confirm"))) return;
    try {
      await api.delete(`/threads/${threadId}`);
      navigate(`/forum/${thread.forum_id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || formatApiError(e);
      window.alert(msg);
    }
  };

  const toggleHide = async (p) => {
    const verb = p.is_hidden ? t("thread.toggle_hide_verb_show") : t("thread.toggle_hide_verb_hide");
    if (!window.confirm(t("thread.toggle_hide_confirm", { verb }))) return;
    try { await api.post(`/posts/${p.id}/hide`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const toggleLike = async (id) => {
    try { await api.post(`/posts/${id}/like`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const setBest = async (postId) => {
    try { await api.post(`/threads/${threadId}/best-answer/${postId}`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const toggleSticky = async () => {
    try { await api.post(`/threads/${threadId}/sticky`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };
  const toggleLock = async () => {
    try { await api.post(`/threads/${threadId}/lock`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const openMoveModal = async () => {
    setShowMove(true);
    setMoveForumId("");
    setMoveReason("");
    try {
      const r = await api.get("/forum");
      setForumList(r.data || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  };
  const submitMove = async () => {
    if (!moveForumId) return;
    setMoving(true);
    try {
      await api.post(`/threads/${threadId}/move`, { forum_id: moveForumId, reason: moveReason });
      try { (await import("sonner")).toast.success("Tråden er flyttet"); } catch {}
      setShowMove(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || formatApiError(e);
      try { (await import("sonner")).toast.error(detail); } catch { setError(detail); }
    } finally {
      setMoving(false);
    }
  };
  const toggleBookmark = async () => {
    try { await api.post(`/threads/${threadId}/bookmark`); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const addModerator = async () => {
    if (!newModName.trim()) return;
    try {
      await api.post(`/threads/${threadId}/moderators`, { username: newModName.trim() });
      setNewModName("");
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const removeModerator = async (uid) => {
    if (!window.confirm(t("thread.mod_remove_confirm"))) return;
    try {
      await api.delete(`/threads/${threadId}/moderators/${uid}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const togglePickedReply = (iid) => {
    setImageIds((prev) => prev.includes(iid) ? prev.filter((x) => x !== iid) : prev.length >= 8 ? prev : [...prev, iid]);
  };
  const togglePickedEdit = (iid) => {
    setEditImageIds((prev) => prev.includes(iid) ? prev.filter((x) => x !== iid) : prev.length >= 8 ? prev : [...prev, iid]);
  };

  if (!data) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div></Layout>;
  const { thread, forum, posts } = data;
  const canModerate = thread.viewer_is_moderator || isAdmin;

  return (
    <Layout>
      <TranslationProgressBanner />
      <div className="font-meta mb-4">
        <Link to="/forum" className="hover:text-[var(--cream)]">{t("thread.breadcrumb_forum")}</Link>
        <span className="mx-2">/</span>
        <Link to={`/forum/${forum.id}`} className="hover:text-[var(--cream)]">{forum.name}</Link>
      </div>

      <section
        className="border border-white/10 rounded-sm overflow-hidden max-w-4xl"
        style={{ background: "rgba(255, 255, 255, 0.025)" }}
        data-testid="thread-panel"
      >
      <article className="px-6 py-6 border-b border-white/10"
               style={{
                 borderLeft: `4px solid var(--cognac)`,
                 background: "rgba(255, 255, 255, 0.045)",
               }}>
        <div className="font-meta mb-3 flex items-center gap-3 flex-wrap" style={{ color: forum.color || "var(--magenta)" }}>
          {forum.name}
          {thread.is_pinned && (
            <span className="inline-flex items-center gap-1 text-[var(--magenta)]" title={t("thread.title_pinned")}>
              <Pin className="h-3 w-3" /> {t("thread.label_pinned")}
            </span>
          )}
          {thread.is_locked && (
            <span className="inline-flex items-center gap-1 text-[var(--cream-faint)]" title={t("thread.title_locked")}>
              <Lock className="h-3 w-3" /> {t("thread.label_locked")}
            </span>
          )}
          {(thread.moderators || []).length > 0 && (
            <span className="inline-flex items-center gap-1 text-[var(--cream-soft)]" title={t("thread.title_thread_mods")}>
              <Shield className="h-3 w-3" /> {t("thread.mod_count", { count: thread.moderators.length })}
            </span>
          )}
        </div>
        <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)] leading-[1.1] mb-5">
          {thread.title}
        </h1>
        <div className="font-meta flex items-center gap-4 flex-wrap">
          <span><MessageSquare className="h-3 w-3 inline mr-1.5" />{t("thread.posts_n", { count: thread.post_count })}</span>
          <span><Eye className="h-3 w-3 inline mr-1.5" />{t("thread.views_n", { count: thread.view_count })}</span>
          {isLoggedIn && (
            <>
              <button onClick={toggleBookmark} className="hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="bookmark-btn">
                {thread.is_bookmarked
                  ? <><BookmarkCheck className="h-3 w-3 text-[var(--magenta)]" /> {t("thread.bookmark_remove")}</>
                  : <><Bookmark className="h-3 w-3" /> {t("thread.bookmark")}</>}
              </button>
              {canModerate && (
                <>
                  <button onClick={toggleSticky} className="hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="sticky-btn">
                    <Pin className="h-3 w-3" /> {thread.is_pinned ? t("thread.unpin") : t("thread.pin")}
                  </button>
                  <button onClick={toggleLock} className="hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="lock-btn">
                    <Lock className="h-3 w-3" /> {thread.is_locked ? t("thread.unlock") : t("thread.lock")}
                  </button>
                  <button onClick={openMoveModal} className="hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="move-thread-btn">
                    <FolderInput className="h-3 w-3" /> Flytt tråd
                  </button>
                </>
              )}
              {isAdmin && (
                <button onClick={() => setShowMods((v) => !v)} className="hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="moderators-btn">
                  <Shield className="h-3 w-3" /> {t("thread.moderators_btn")}
                </button>
              )}
              {(isAdmin || (user && thread.author_id === user.id)) && (
                <button
                  onClick={deleteThread}
                  className="hover:text-red-400 inline-flex items-center gap-1.5"
                  data-testid="delete-thread-btn"
                  title={
                    isAdmin
                      ? t("thread.delete_thread_admin_tip")
                      : t("thread.delete_thread_owner_tip")
                  }
                >
                  <Trash2 className="h-3 w-3" /> {t("thread.delete_thread_btn")}
                </button>
              )}
            </>
          )}
        </div>

        {/* Moderator-panel (kun admin) */}
        {isAdmin && showMods && (
          <div className="mt-5 p-4 border border-white/10 rounded-sm bg-[var(--noir-elev)]/50" data-testid="moderators-panel">
            <div className="font-meta text-xs text-[var(--magenta)] mb-3 uppercase tracking-wider">{t("thread.mods_section_title")}</div>
            {(thread.moderators || []).length === 0 ? (
              <div className="font-manrope text-sm text-[var(--cream-soft)] mb-3">{t("thread.no_moderators_yet")}</div>
            ) : (
              <ul className="space-y-2 mb-4">
                {thread.moderators.map((m) => (
                  <li key={m.user_id} className="flex items-center justify-between gap-3" data-testid={`mod-${m.user_id}`}>
                    <Link to={`/profile/${m.username}`} className="font-fraunces italic text-[var(--cream)] hover:text-[var(--magenta)]">
                      @{m.username}
                    </Link>
                    <button
                      onClick={() => removeModerator(m.user_id)}
                      className="font-meta text-xs text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                      data-testid={`remove-mod-${m.user_id}`}
                    >
                      {t("thread.mod_remove")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                value={newModName}
                onChange={(e) => setNewModName(e.target.value)}
                placeholder={t("thread.mod_username_placeholder")}
                className="input-editorial flex-1"
                data-testid="add-mod-input"
              />
              <button onClick={addModerator} className="btn-magenta text-xs inline-flex items-center gap-1" data-testid="add-mod-btn">
                <Plus className="h-3 w-3" /> {t("thread.mod_add")}
              </button>
            </div>
          </div>
        )}
      </article>

      <div className="divide-y divide-white/5" data-testid="post-list">
        {posts.map((p, idx) => {
          const isOwn = isLoggedIn && p.author_id === user.id;
          const canDelete = !p.is_op && (isOwn || isAdmin);
          const isBest = thread.best_answer_post_id === p.id;
          const canMarkBest = isLoggedIn && thread.author_id === user.id && !p.is_op;
          const editingThis = editingId === p.id;
          return (
            <article
              key={p.id}
              id={`post-${p.id}`}
              className={`grid grid-cols-12 gap-6 px-6 py-6 scroll-mt-24 ${isBest ? "ring-1 ring-[var(--magenta)] ring-inset bg-[var(--magenta)]/[0.05]" : ""} ${p.is_hidden ? "opacity-50" : ""}`}
              style={!isBest ? { background: idx % 2 === 1 ? "rgba(255, 255, 255, 0.015)" : "rgba(255, 255, 255, 0.025)" } : undefined}
              data-testid={`post-${p.id}`}
            >
              <div className="col-span-12 sm:col-span-3">
                <div className="flex sm:flex-col items-center sm:items-start gap-3">
                  <Avatar className="h-12 w-12 rounded-sm border border-white/10">
                    <AvatarFallback className="bg-[var(--magenta)] text-black font-bold rounded-sm">
                      {(p.author?.username || "?").slice(0,2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    {p.couple_author ? (
                      <>
                        <Link to={`/par/${p.couple_author.handle}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]" data-testid={`post-couple-author-${p.id}`}>
                          {p.couple_author.name}
                        </Link>
                        <div className="font-meta text-[10px] text-[var(--magenta)] mt-0.5 inline-flex items-center gap-1">
                          {t("thread.couple_posted_by")} <Link to={`/profile/${p.author?.username}`} className="hover:text-[var(--cream)] underline-offset-2 hover:underline">@{p.author?.username}</Link>
                        </div>
                      </>
                    ) : (
                      <Link to={`/profile/${p.author?.username}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]">
                        {p.author?.username}
                      </Link>
                    )}
                    {p.author?.role === "admin" && (
                      <div className="font-meta text-[var(--magenta)] mt-1">{t("thread.admin_role_label")}</div>
                    )}
                    <div className="font-meta mt-1">{t("thread.post_count_short", { count: p.author?.post_count ?? 0 })}</div>
                  </div>
                </div>
              </div>

              <div className="col-span-12 sm:col-span-9 border-l-0 sm:border-l border-white/10 sm:pl-6">
                <div className="flex items-center justify-between mb-4 font-meta gap-3 flex-wrap">
                  <span className="flex items-center gap-2 flex-wrap">
                    {idx === 0 ? t("thread.post_first") : t("thread.reply_n", { n: idx })} · {formatRelative(p.created_at)}
                    {p.is_edited && <span className="text-[var(--cream-faint)]">· {t("thread.edited")}</span>}
                    {p.is_unread && <span className="text-[var(--magenta)]">· {t("thread.unread_label")}</span>}
                    {p.is_hidden && (
                      <span className="inline-flex items-center gap-1 text-[var(--magenta)]" title={t("thread.hidden_by_mod")}>
                        <EyeOff className="h-3 w-3" /> {t("thread.hidden_label")}
                      </span>
                    )}
                    {isBest && <span className="inline-flex items-center gap-1 text-[var(--magenta)]"><Award className="h-3 w-3" /> {t("thread.best_answer_label")}</span>}
                  </span>
                  <ReportButton targetType="post" targetId={p.id} />
                </div>

                {editingThis ? (
                  <div data-testid={`edit-form-${p.id}`}>
                    <MarkdownToolbar
                      textareaRef={editRef}
                      value={editBody}
                      onChange={setEditBody}
                      onAddImage={() => setEditPickerFor(p.id)}
                    />
                    <textarea
                      ref={editRef}
                      value={editBody}
                      onChange={(e)=>setEditBody(e.target.value)}
                      rows={6}
                      className="input-editorial mb-3"
                      data-testid={`edit-textarea-${p.id}`}
                    />
                    {editImageIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3 font-meta text-xs">
                        <AtSign className="h-3 w-3 hidden" />
                        <span className="text-[var(--cream-soft)] self-center">{editImageIds.length === 1 ? t("thread.images_attached_one", { count: 1 }) : t("thread.images_attached_other", { count: editImageIds.length })}</span>
                        <button
                          type="button"
                          onClick={() => setEditImageIds([])}
                          className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1"
                          data-testid={`clear-edit-images-${p.id}`}
                        >
                          <X className="h-3 w-3" /> {t("thread.clear")}
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={()=>saveEdit(p.id)} className="btn-magenta" data-testid={`save-edit-${p.id}`}>{t("thread.save")}</button>
                      <button onClick={cancelEdit} className="btn-ghost">{t("thread.cancel")}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <PostBody body={p.body} mentions={p.mentions} />
                    <PostImages images={p.images || []} />
                  </>
                )}

                {/* Action bar */}
                {isLoggedIn && !editingThis && (
                  <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/5 font-meta flex-wrap">
                    {!isOwn && (
                      <button
                        onClick={() => toggleLike(p.id)}
                        className={`inline-flex items-center gap-1.5 transition-colors ${p.user_liked ? "text-[var(--magenta)]" : "text-[var(--cream-soft)] hover:text-[var(--magenta)]"}`}
                        data-testid={`like-${p.id}`}
                      >
                        <Heart className={`h-3 w-3 ${p.user_liked ? "fill-[var(--magenta)]" : ""}`} />
                        {p.like_count || 0}
                      </button>
                    )}
                    {!thread.is_locked && (
                      <button onClick={()=>quote(p)} className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid={`quote-${p.id}`}>
                        <Quote className="h-3 w-3" /> {t("thread.quote")}
                      </button>
                    )}
                    {canMarkBest && (
                      <button onClick={()=>setBest(p.id)} className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid={`best-${p.id}`}>
                        <Award className="h-3 w-3" /> {isBest ? t("thread.remove_best") : t("thread.mark_best")}
                      </button>
                    )}
                    {isOwn && (
                      <button onClick={()=>startEdit(p)} className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid={`edit-${p.id}`}>
                        <Edit3 className="h-3 w-3" /> {t("thread.edit")}
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={()=>deletePost(p.id)} className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid={`delete-${p.id}`}>
                        <Trash2 className="h-3 w-3" /> {t("thread.delete")}
                      </button>
                    )}
                    {canModerate && !isOwn && (
                      <button
                        onClick={() => toggleHide(p)}
                        className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
                        data-testid={`hide-${p.id}`}
                      >
                        <EyeOff className="h-3 w-3" /> {p.is_hidden ? t("thread.show_again") : t("thread.hide")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <form onSubmit={submitReply} className="px-6 py-8 border-t border-white/10" data-testid="reply-form"
            style={{ background: "rgba(255, 255, 255, 0.015)" }}>
        <div className="font-meta mb-4">{t("thread.reply_section")}</div>
        {!isLoggedIn ? (
          <div className="font-manrope text-sm text-[var(--cream-soft)]">
            <Link to="/login" className="text-[var(--magenta)] underline-offset-4 hover:underline">{t("thread.login_to_reply_prefix")}</Link> {t("thread.login_to_reply_suffix")}
          </div>
        ) : thread.is_locked ? (
          <div className="font-fraunces italic text-[var(--cream-soft)]">
            <Lock className="h-4 w-4 inline mr-2" /> {t("thread.locked_full")}
          </div>
        ) : (
          <>
            {postingAsCouple && (
              <div className="bg-[var(--magenta)]/[0.08] border-l-2 border-[var(--magenta)] px-3 py-2 mb-4 flex items-center gap-2 text-xs font-manrope" data-testid="ctx-banner-reply">
                <span className="font-meta text-[var(--magenta)]">{t("thread.replying_as_couple")}</span>
                <span className="text-[var(--cream)]">· {linkedCouple.name}</span>
              </div>
            )}
            <MarkdownToolbar
              textareaRef={replyRef}
              value={body}
              onChange={setBody}
              onAddImage={() => setPickerOpen(true)}
            />
            <textarea
              ref={replyRef}
              value={body}
              onChange={(e)=>setBody(e.target.value)}
              rows={6}
              placeholder={t("thread.reply_placeholder")}
              className="input-editorial mb-4"
              required
              data-testid="reply-body"
            />
            {imageIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4 font-meta text-xs">
                <span className="text-[var(--cream-soft)]">{imageIds.length === 1 ? t("thread.images_attached_one", { count: 1 }) : t("thread.images_attached_other", { count: imageIds.length })}</span>
                <button
                  type="button"
                  onClick={() => setImageIds([])}
                  className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1"
                  data-testid="clear-reply-images"
                >
                  <X className="h-3 w-3" /> {t("thread.remove_all")}
                </button>
              </div>
            )}
            {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mb-3">{error}</div>}
            <button type="submit" disabled={loading} className="btn-magenta" data-testid="reply-submit">
              {loading ? t("thread.post_loading") : t("thread.send")}
            </button>
          </>
        )}
      </form>
      </section>

      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={togglePickedReply}
        alreadyPicked={imageIds}
      />
      <ImagePicker
        open={!!editPickerFor}
        onClose={() => setEditPickerFor(null)}
        onPick={togglePickedEdit}
        alreadyPicked={editImageIds}
      />

      {/* Flytt-tråd-modal */}
      <Dialog open={showMove} onOpenChange={setShowMove}>
        <DialogContent className="max-w-lg" data-testid="move-thread-modal">
          <DialogHeader>
            <DialogTitle>Flytt tråd til et annet underforum</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                MÅLFORUM
              </label>
              <select
                value={moveForumId}
                onChange={(e) => setMoveForumId(e.target.value)}
                className="input-editorial w-full"
                data-testid="move-thread-target"
              >
                <option value="">Velg forum …</option>
                {forumList.map((cat) => (
                  <optgroup key={cat.category?.id} label={cat.category?.name || ""}>
                    {(cat.forums || [])
                      .filter((f) => f.id !== thread.forum_id)
                      .map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                BEGRUNNELSE (valgfritt – sendes som varsel til trådstarter)
              </label>
              <textarea
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                rows={3}
                maxLength={300}
                placeholder="F.eks. «Denne hører bedre hjemme i Humor og moro.»"
                className="input-editorial w-full"
                data-testid="move-thread-reason"
              />
            </div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)]">
              Trådstarter får et varsel om flyttingen{moveReason ? " med din begrunnelse" : ""}.
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowMove(false)}
              className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-meta border border-white/15 text-[var(--cream-soft)]"
              data-testid="move-thread-cancel"
            >
              Avbryt
            </button>
            <button
              onClick={submitMove}
              disabled={!moveForumId || moving}
              className="btn-magenta disabled:opacity-40"
              data-testid="move-thread-submit"
            >
              {moving ? "FLYTTER…" : "FLYTT TRÅD"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
